import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ArrowForward as ArrowForwardIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { apiService } from '../api';

const Sequencer = () => {
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState('');
  const [documents, setDocuments] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sequence, setSequence] = useState(null);
  const [previousSequences, setPreviousSequences] = useState([]);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedSequenceToView, setSelectedSequenceToView] = useState(null);

  // Load cases on mount
  useEffect(() => {
    loadCases();
  }, []);

  // Load documents when case is selected
  useEffect(() => {
    if (selectedCase) {
      loadDocuments(selectedCase);
      loadPreviousSequences(selectedCase);
    } else {
      setDocuments([]);
      setSelectedDocuments([]);
      setPreviousSequences([]);
    }
  }, [selectedCase]);

  const loadCases = async () => {
    try {
      const response = await apiService.getCases();
      setCases(response.data.cases || []);
    } catch (err) {
      console.error('Error loading cases:', err);
      setError('Failed to load cases');
    }
  };

  const loadDocuments = async (caseId) => {
    try {
      const response = await apiService.getCaseDocuments(caseId);
      setDocuments(response.data.documents || []);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Failed to load documents');
    }
  };

  const loadPreviousSequences = async (caseId) => {
    try {
      const response = await apiService.getCaseSequences(caseId);
      setPreviousSequences(response.data.sequences || []);
    } catch (err) {
      console.error('Error loading sequences:', err);
    }
  };

  const handleDocumentToggle = (documentId) => {
    setSelectedDocuments(prev => {
      if (prev.includes(documentId)) {
        return prev.filter(id => id !== documentId);
      } else {
        return [...prev, documentId];
      }
    });
  };



  const handleCreateSequence = async () => {
    if (!selectedCase) {
      setError('Please select a case');
      return;
    }
    if (selectedDocuments.length === 0) {
      setError('Please select at least one document');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiService.createSequence(selectedCase, selectedDocuments);
      setSequence(response.data);
      loadPreviousSequences(selectedCase);
      setSelectedDocuments([]);
    } catch (err) {
      console.error('Error creating sequence:', err);
      setError(err.response?.data?.error || 'Failed to create sequence');
    } finally {
      setLoading(false);
    }
  };

  const handleViewSequence = async (sequenceId) => {
    try {
      const response = await apiService.getSequence(sequenceId);
      setSelectedSequenceToView(response.data.sequence);
      setViewDialogOpen(true);
    } catch (err) {
      console.error('Error loading sequence:', err);
      setError('Failed to load sequence');
    }
  };

  const handleDeleteSequence = async (sequenceId) => {
    if (!window.confirm('Are you sure you want to delete this sequence?')) {
      return;
    }

    try {
      await apiService.deleteSequence(sequenceId);
      loadPreviousSequences(selectedCase);
      if (sequence?.sequence_id === sequenceId) {
        setSequence(null);
      }
    } catch (err) {
      console.error('Error deleting sequence:', err);
      setError('Failed to delete sequence');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <TimelineIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Document Sequencer
        </Typography>
      </Box>

      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
        Create chronological sequences of events from case documents. The AI will extract key events,
        dates, people involved, and descriptions to create an interactive timeline.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Case and Document Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Select Case and Documents
        </Typography>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Select Case</InputLabel>
          <Select
            value={selectedCase}
            onChange={(e) => setSelectedCase(e.target.value)}
            label="Select Case"
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {cases.map((caseItem) => (
              <MenuItem key={caseItem.id} value={caseItem.id}>
                {caseItem.case_number} - {caseItem.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedCase && documents.length > 0 && (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              Select Documents to Analyze:
            </Typography>
            <FormGroup>
              {documents.map((doc) => (
                <FormControlLabel
                  key={doc.id}
                  control={
                    <Checkbox
                      checked={selectedDocuments.includes(doc.id)}
                      onChange={() => handleDocumentToggle(doc.id)}
                    />
                  }
                  label={`${doc.name || doc.filename || 'Unnamed Document'} (${doc.type || doc.file_type || 'unknown'})`}
                />
              ))}
            </FormGroup>
          </Box>
        )}

        {selectedCase && documents.length === 0 && (
          <Alert severity="info">
            No documents found for this case. Please upload documents first.
          </Alert>
        )}

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleCreateSequence}
            disabled={loading || !selectedCase || selectedDocuments.length === 0}
            startIcon={loading ? <CircularProgress size={20} /> : <ArrowForwardIcon />}
          >
            {loading ? 'Creating Sequence...' : 'Create Sequence'}
          </Button>
        </Box>
      </Paper>

      {/* Horizontal Timeline Visualization */}
      {sequence && sequence.events && sequence.events.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Chronological Timeline
          </Typography>
          
          {/* Summary Stats */}
          {sequence.summary && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Total Events</Typography>
                  <Typography variant="h6">{sequence.summary.totalEvents || 0}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Date Range</Typography>
                  <Typography variant="body2">{sequence.summary.dateRange || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Documents Analyzed</Typography>
                  <Typography variant="h6">{sequence.summary.documentsAnalyzed || 0}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">People Involved</Typography>
                  <Typography variant="h6">{sequence.summary.peopleInvolved?.length || 0}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Horizontal Timeline */}
          <Box sx={{ 
            overflowX: 'auto', 
            overflowY: 'hidden',
            pb: 2,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: 4,
              '&:hover': {
                backgroundColor: '#555',
              },
            },
          }}>
            <Box sx={{ 
              display: 'flex', 
              gap: 3, 
              position: 'relative',
              minWidth: 'fit-content',
              pb: 2
            }}>
              {/* Timeline Line */}
              <Box sx={{
                position: 'absolute',
                top: 60,
                left: 0,
                right: 0,
                height: 3,
                bgcolor: 'primary.main',
                zIndex: 0
              }} />

              {/* Event Cards */}
              {sequence.events.map((event, index) => (
                <Box 
                  key={index}
                  sx={{ 
                    position: 'relative',
                    minWidth: 280,
                    maxWidth: 280,
                    zIndex: 1
                  }}
                >
                  {/* Timeline Dot */}
                  <Box sx={{
                    position: 'absolute',
                    top: 50,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    border: '4px solid white',
                    boxShadow: 2,
                    zIndex: 2
                  }} />
                  
                  {/* Sequence Number */}
                  <Box sx={{
                    position: 'absolute',
                    top: 52,
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    zIndex: 3
                  }}>
                    {index + 1}
                  </Box>

                  {/* Event Card */}
                  <Card 
                    sx={{ 
                      mt: 10,
                      boxShadow: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <CardContent>
                      {/* Date */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <CalendarIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 'bold' }}>
                          {event.dateDisplay || event.date || 'Date Unknown'}
                        </Typography>
                      </Box>

                      {/* Title */}
                      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, fontSize: '1rem' }}>
                        {event.title}
                      </Typography>

                      {/* Description */}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {event.description}
                      </Typography>

                      {/* People Involved */}
                      {event.people && event.people.length > 0 && (
                        <Box sx={{ mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                              People Involved:
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {event.people.slice(0, 3).map((person, idx) => (
                              <Chip
                                key={idx}
                                label={person}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            ))}
                            {event.people.length > 3 && (
                              <Chip
                                label={`+${event.people.length - 3} more`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        </Box>
                      )}

                      {/* Location */}
                      {event.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <LocationIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {event.location}
                          </Typography>
                        </Box>
                      )}

                      {/* Event Type & Document Source */}
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 2, flexWrap: 'wrap' }}>
                        {event.eventType && (
                          <Chip
                            label={event.eventType.replace('_', ' ')}
                            size="small"
                            color="primary"
                            sx={{ textTransform: 'capitalize', fontSize: '0.7rem' }}
                          />
                        )}
                        {event.documentSource && (
                          <Chip
                            label={event.documentSource}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Arrow to Next Event */}
                  {index < sequence.events.length - 1 && (
                    <Box sx={{
                      position: 'absolute',
                      top: 60,
                      right: -20,
                      transform: 'translateY(-50%)',
                      zIndex: 1
                    }}>
                      <ArrowForwardIcon color="primary" />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      )}

      {/* Event Details List */}
      {sequence && sequence.events && sequence.events.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Event Details
          </Typography>
          <List>
            {sequence.events.map((event, index) => (
              <React.Fragment key={index}>
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6">{event.title}</Typography>
                        <Chip label={event.date || 'Date Unknown'} size="small" color="primary" />
                        {event.event_type && (
                          <Chip label={event.event_type} size="small" variant="outlined" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {event.description}
                        </Typography>
                        {event.people_involved && event.people_involved.length > 0 && (
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              People Involved: 
                            </Typography>
                            {event.people_involved.map((person, idx) => (
                              <Chip
                                key={idx}
                                label={person}
                                size="small"
                                sx={{ ml: 0.5, mb: 0.5 }}
                              />
                            ))}
                          </Box>
                        )}
                        {event.document_source && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                            Source: {event.document_source}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < sequence.events.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* Previous Sequences */}
      {previousSequences.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Previous Sequences
          </Typography>
          <List>
            {previousSequences.map((seq) => (
              <Card key={seq.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Sequence from {formatDate(seq.created_at)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {seq.events?.length || 0} events from {seq.document_ids?.length || 0} documents
                      </Typography>
                    </Box>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleViewSequence(seq.id)}
                        color="primary"
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteSequence(seq.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </List>
        </Paper>
      )}

      {/* View Sequence Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>Sequence Details</DialogTitle>
        <DialogContent>
          {selectedSequenceToView && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Created: {formatDate(selectedSequenceToView.created_at)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Events: {selectedSequenceToView.events?.length || 0} | 
                Documents: {selectedSequenceToView.document_ids?.length || 0}
              </Typography>

              {/* Summary Stats */}
              {selectedSequenceToView.summary && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Total Events</Typography>
                      <Typography variant="h6">{selectedSequenceToView.summary.totalEvents || 0}</Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Date Range</Typography>
                      <Typography variant="body2">{selectedSequenceToView.summary.dateRange || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Documents</Typography>
                      <Typography variant="h6">{selectedSequenceToView.summary.documentsAnalyzed || 0}</Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">People</Typography>
                      <Typography variant="h6">{selectedSequenceToView.summary.peopleInvolved?.length || 0}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Horizontal Timeline */}
              <Box sx={{ 
                overflowX: 'auto', 
                overflowY: 'hidden',
                pb: 2,
                maxHeight: 500,
                '&::-webkit-scrollbar': {
                  height: 8,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                  borderRadius: 4,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#888',
                  borderRadius: 4,
                  '&:hover': {
                    backgroundColor: '#555',
                  },
                },
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 3, 
                  position: 'relative',
                  minWidth: 'fit-content',
                  pb: 2
                }}>
                  {/* Timeline Line */}
                  <Box sx={{
                    position: 'absolute',
                    top: 60,
                    left: 0,
                    right: 0,
                    height: 3,
                    bgcolor: 'primary.main',
                    zIndex: 0
                  }} />

                  {/* Event Cards */}
                  {selectedSequenceToView.events?.map((event, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        position: 'relative',
                        minWidth: 280,
                        maxWidth: 280,
                        zIndex: 1
                      }}
                    >
                      {/* Timeline Dot */}
                      <Box sx={{
                        position: 'absolute',
                        top: 50,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        border: '4px solid white',
                        boxShadow: 2,
                        zIndex: 2
                      }} />
                      
                      <Box sx={{
                        position: 'absolute',
                        top: 52,
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'white',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        zIndex: 3
                      }}>
                        {index + 1}
                      </Box>

                      {/* Event Card */}
                      <Card 
                        sx={{ 
                          mt: 10,
                          boxShadow: 3,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: 6,
                            transform: 'translateY(-4px)'
                          }
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <CalendarIcon fontSize="small" color="primary" />
                            <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 'bold' }}>
                              {event.dateDisplay || event.date || 'Date Unknown'}
                            </Typography>
                          </Box>

                          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, fontSize: '1rem' }}>
                            {event.title}
                          </Typography>

                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {event.description}
                          </Typography>

                          {event.people && event.people.length > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                                  People:
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {event.people.slice(0, 2).map((person, idx) => (
                                  <Chip
                                    key={idx}
                                    label={person}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                ))}
                                {event.people.length > 2 && (
                                  <Chip
                                    label={`+${event.people.length - 2}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                            </Box>
                          )}

                          {event.location && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                              <LocationIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {event.location}
                              </Typography>
                            </Box>
                          )}

                          <Box sx={{ display: 'flex', gap: 0.5, mt: 2, flexWrap: 'wrap' }}>
                            {event.eventType && (
                              <Chip
                                label={event.eventType.replace('_', ' ')}
                                size="small"
                                color="primary"
                                sx={{ textTransform: 'capitalize', fontSize: '0.7rem' }}
                              />
                            )}
                            {event.documentSource && (
                              <Chip
                                label={event.documentSource}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        </CardContent>
                      </Card>

                      {index < selectedSequenceToView.events.length - 1 && (
                        <Box sx={{
                          position: 'absolute',
                          top: 60,
                          right: -20,
                          transform: 'translateY(-50%)',
                          zIndex: 1
                        }}>
                          <ArrowForwardIcon color="primary" />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Sequencer;
